import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { Grid, Link, Tooltip } from '@mui/material';
import { Button, Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { Prompt } from 'react-router-dom';
import { RightsContext } from 'providers/RightsContext';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import curry from 'lodash.curry';
import FacilityKpiIndicatorsContainer from 'features/facility/edit/kpiIndicators/kpiIndicators/components/FacilityKpiIndicatorsContainer';
import { get } from '@totalsoft/change-tracking-react';
import { GridActionsWrapper } from 'components/common/CommonStyles';

const FacilityOfferAdditionalDataComponent = ({
  t,
  onPropertyChange,
  bankList,
  onSave,
  saving,
  isDirty,
  handleFacilityStateChange,
  openLinkedDocument,
  validation,
  lens
}) => {
  const { readOnlyFull, isImage, clientInvoicingWrite, clientInvoicingRead } = useContext(RightsContext);

  const handlePropertyChange = curry((prop, value) => {
    onPropertyChange(prop, value);
  });

  return (
    <>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <Grid container spacing={4} px={1}>
        <Grid item xs={12}>
          <span> {t('OfferEdit.AdditionalData.FacilityNumber')}</span>
          <span>
            <Tooltip title={lens.additionalData.transactionTypeName |> get}>
              <Link
                style={{ cursor: 'pointer' }}
                onClick={openLinkedDocument(lens.additionalData.facilityId |> get, lens.additionalData.facilitySiteId |> get)}
              >
                {lens.additionalData.facilityNumber |> get}
              </Link>
            </Tooltip>
          </span>
          <span>
            {' '}
            {' / ' +
              (lens.additionalData.facilityStateName |> get) +
              ' / ' +
              t('DATE_FORMAT', { date: { value: lens.additionalData.facilityDate |> get, format: 'DD.MM.YYYY' } })}
          </span>
        </Grid>
        {clientInvoicingRead && (
          <Grid item xs={6}>
            <Autocomplete
              id={'offerAdditionalDataCard_clientInvoicingBank'}
              disabled={readOnlyFull || !clientInvoicingWrite}
              fullWidth
              value={(lens.additionalData.clientInvoicingBankId |> get) || ''}
              valueKey='bankId'
              labelKey='bankName'
              label={t('OfferEdit.AdditionalData.ClientInvoicingBank')}
              options={bankList}
              onChange={handlePropertyChange('clientInvoicingBankId')}
              isClearable={false}
              simpleValue
            />
          </Grid>
        )}
        {clientInvoicingRead && (
          <Grid item xs={6}>
            <TextField
              id={'offerAdditionalDataCard_clientConversionFactor'}
              fullWidth
              isNumeric
              value={(lens.additionalData.clientConversionFactor |> get) || 0}
              onChange={handlePropertyChange('clientConversionFactor')}
              label={t('OfferEdit.AdditionalData.ClientConversionFactor')}
              error={!isValid(validation.clientConversionFactor)}
              helperText={getErrors(validation.clientConversionFactor)}
              disabled={!clientInvoicingWrite}
            />
          </Grid>
        )}
        <FacilityKpiIndicatorsContainer
          documentId={lens.additionalData.facilityId |> get}
          siteId={lens.additionalData.facilitySiteId |> get}
          isFromOffer={true}
          hasFacilityOffer={true}
          onParentFacilityStateChange={handleFacilityStateChange}
          shouldSaveFromParent={lens.facilityState?.facilityDataShouldSave |> get}
        />
        {!isImage && (
          <GridActionsWrapper item xs={12}>
            <Button
              id={'offerAdditionalDataCard_saveBtn'}
              color='primary'
              variant='contained'
              onClick={onSave}
              disabled={readOnlyFull || saving || !isDirty || !clientInvoicingWrite}
            >
              {saving ? t('Saving') + '...' : t('Save')}
            </Button>
          </GridActionsWrapper>
        )}
      </Grid>
    </>
  );
};

FacilityOfferAdditionalDataComponent.propTypes = {
  t: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  bankList: PropTypes.array.isRequired,
  isDirty: PropTypes.bool.isRequired,
  handleFacilityStateChange: PropTypes.func.isRequired,
  openLinkedDocument: PropTypes.func,
  validation: PropTypes.object,
  lens: PropTypes.object.isRequired
};

export default compose(withTranslation())(FacilityOfferAdditionalDataComponent);
